import dayjs from 'dayjs';

export const exportExcelWorkOrder = async (data = [], t) => {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet(t ? t('vehicle-maintenance', 'Vehicle Maintenance') : 'Vehicle Maintenance', {
    views: [{ state: 'frozen', ySplit: 4 }]
  });

  // =====================================================
  // PAGE SETUP
  // =====================================================
  worksheet.pageSetup = {
    paperSize: 9,
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.5,
      bottom: 0.5,
      header: 0.3,
      footer: 0.3
    }
  };

  // =====================================================
  // STYLES
  // =====================================================
  const BORDER_THIN = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
  };

  const BORDER_MEDIUM = {
    top: { style: 'medium' },
    left: { style: 'medium' },
    bottom: { style: 'medium' },
    right: { style: 'medium' }
  };

  const HEADER_FILL = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: '4F5CCF' }
  };

  const GROUP_FILL = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: '3949AB' }
  };

  const HEADER_FONT = {
    bold: true,
    color: { argb: 'FFFFFF' },
    size: 11,
    name: 'Calibri'
  };

  const BODY_FONT = {
    name: 'Calibri',
    size: 10
  };

  const ALIGN_CENTER = {
    vertical: 'middle',
    horizontal: 'center',
    wrapText: true
  };

  const ALIGN_LEFT = {
    vertical: 'middle',
    horizontal: 'left',
    wrapText: true
  };

  const ALIGN_RIGHT = {
    vertical: 'middle',
    horizontal: 'right',
    wrapText: true
  };

  // =====================================================
  // COLUMN WIDTH
  // =====================================================
  worksheet.columns = [
    { width: 6 }, // No
    { width: 22 }, // WO Number
    { width: 18 }, // Vehicle
    { width: 15 }, // WO Date
    { width: 15 }, // Estimate Date
    { width: 28 }, // Workshop
    { width: 14 }, // Status
    { width: 16 }, // Total
    { width: 28 }, // Task
    { width: 16 }, // Schedule By
    { width: 18 }, // Next Service Odo
    { width: 18 }, // Next Service Date
    { width: 28 }, // Remark
    { width: 18 } // Service Cost
  ];

  // =====================================================
  // TITLE
  // =====================================================
  worksheet.mergeCells('A1:N1');

  const titleCell = worksheet.getCell('A1');

  titleCell.value = t ? t('workOrder.exportTitle', 'VEHICLE MAINTENANCE / WORK ORDER REPORT') : 'VEHICLE MAINTENANCE / WORK ORDER REPORT';

  titleCell.font = {
    bold: true,
    size: 15,
    name: 'Calibri'
  };

  titleCell.alignment = {
    horizontal: 'center',
    vertical: 'middle'
  };

  worksheet.getRow(1).height = 28;

  // =====================================================
  // GROUP HEADER
  // =====================================================
  worksheet.mergeCells('I2:N2');

  const groupHeaderCell = worksheet.getCell('I2');

  groupHeaderCell.value = t ? t('workOrder.serviceType', 'Service Type') : 'Service Type';

  groupHeaderCell.font = {
    bold: true,
    size: 12,
    color: { argb: 'FFFFFF' },
    name: 'Calibri'
  };

  groupHeaderCell.fill = GROUP_FILL;

  groupHeaderCell.alignment = ALIGN_CENTER;

  groupHeaderCell.border = BORDER_MEDIUM;

  worksheet.getRow(2).height = 24;

  // =====================================================
  // HEADER
  // =====================================================
  const headers = [
    t ? t('workOrder.no', 'No') : 'No',
    t ? t('workOrder.woNumber', 'WO Number') : 'WO Number',
    t ? t('workOrder.vehicle', 'Vehicle') : 'Vehicle',
    t ? t('workOrder.woDate', 'WO Date') : 'WO Date',
    t ? t('workOrder.estimateDate', 'Estimate Date') : 'Estimate Date',
    t ? t('workOrder.workshop', 'Workshop') : 'Workshop',
    t ? t('workOrder.status', 'Status') : 'Status',
    t ? t('workOrder.total', 'Total') : 'Total',
    t ? t('workOrder.task', 'Task') : 'Task',
    t ? t('workOrder.scheduleBy', 'Schedule By') : 'Schedule By',
    t ? t('workOrder.nextServiceOdo', 'Next Service Odo') : 'Next Service Odo',
    t ? t('workOrder.nextServiceDate', 'Next Service Date') : 'Next Service Date',
    t ? t('workOrder.remark', 'Remark') : 'Remark',
    t ? t('workOrder.serviceCost', 'Service Cost') : 'Service Cost'
  ];

  const headerRow = worksheet.getRow(3);

  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);

    cell.value = header;

    cell.font = HEADER_FONT;

    cell.fill = HEADER_FILL;

    cell.border = BORDER_THIN;

    cell.alignment = ALIGN_CENTER;
  });

  headerRow.height = 28;

  // =====================================================
  // DATA
  // =====================================================
  let currentRow = 4;

  data.forEach((maintenance, index) => {
    const tasks = maintenance.vm_service_type || [];

    if (tasks.length === 0) {
      tasks.push({});
    }

    const startRow = currentRow;
    const endRow = currentRow + tasks.length - 1;

    tasks.forEach((task, taskIndex) => {
      const row = worksheet.getRow(currentRow);

      row.height = 20;

      // ===============================================
      // MASTER DATA
      // ===============================================
      if (taskIndex === 0) {
        row.getCell(1).value = index + 1;

        row.getCell(2).value = maintenance.vm_number || '';

        row.getCell(3).value = maintenance.license_plate || '';

        if (maintenance.vm_date) {
          row.getCell(4).value = dayjs(maintenance.vm_date).toDate();

          row.getCell(4).numFmt = 'dd mmm yyyy';
        }

        if (maintenance.date_completed) {
          row.getCell(5).value = dayjs(maintenance.date_completed).toDate();

          row.getCell(5).numFmt = 'dd mmm yyyy';
        }

        row.getCell(6).value = maintenance?.join?.workshop_name || '';

        row.getCell(7).value = (maintenance.status || '').toUpperCase();

        row.getCell(8).value = Number(maintenance.price || 0);

        row.getCell(8).numFmt = '"Rp" #,##0';
      }

      // ===============================================
      // DETAIL DATA
      // ===============================================
      row.getCell(9).value = task.service_type_name || '';

      row.getCell(10).value = task.schedule_by || '';

      row.getCell(11).value = task.next_service_odo || 0;

      if (task.next_service_date && task.next_service_date !== '1900-01-01T00:00:00Z') {
        row.getCell(12).value = dayjs(task.next_service_date).toDate();

        row.getCell(12).numFmt = 'dd mmm yyyy';
      }

      row.getCell(13).value = task.remarks || '';

      row.getCell(14).value = Number(task.price || 0);

      row.getCell(14).numFmt = '"Rp" #,##0';

      // ===============================================
      // STYLE CELLS
      // ===============================================
      for (let col = 1; col <= 14; col++) {
        const cell = row.getCell(col);

        cell.font = BODY_FONT;

        cell.border = BORDER_THIN;

        if ([1, 4, 5, 10, 12].includes(col)) {
          cell.alignment = ALIGN_CENTER;
        } else if ([8, 11, 14].includes(col)) {
          cell.alignment = ALIGN_RIGHT;
        } else {
          cell.alignment = ALIGN_LEFT;
        }
      }

      currentRow++;
    });

    // ===============================================
    // MERGE MASTER CELLS
    // ===============================================
    if (tasks.length > 1) {
      const mergeColumns = [1, 2, 3, 4, 5, 6, 7, 8];

      mergeColumns.forEach((col) => {
        worksheet.mergeCells(startRow, col, endRow, col);
      });
    }
  });

  // =====================================================
  // OUTER BORDER
  // =====================================================
  const lastRow = worksheet.lastRow.number;

  for (let row = 2; row <= lastRow; row++) {
    for (let col = 1; col <= 14; col++) {
      const cell = worksheet.getRow(row).getCell(col);

      if (row === 2 || row === lastRow || col === 1 || col === 14) {
        cell.border = BORDER_MEDIUM;
      }
    }
  }

  // =====================================================
  // AUTO FILTER
  // =====================================================
  worksheet.autoFilter = {
    from: 'A3',
    to: 'N3'
  };

  // =====================================================
  // DOWNLOAD
  // =====================================================
  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');

  link.href = url;

  link.download = `Vehicle_Maintenance_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;

  document.body.appendChild(link);

  link.click();

  link.remove();

  window.URL.revokeObjectURL(url);
};
